import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
  login,
  register,
  UserSession,
  sanitize,
  kebabToCamel,
  kebabToTitle,
  toTitleCase
} from '../../utils'
import { masterTeam, createTeam } from '../../appwrite/methods'
import InfoTooltip from '../../components/InfoTooltip'
import FileUpload from '../../components/FileUpload'
import logo from "../../assets/tom-logo-icon-black.jpg"
import { ID, Client, Account, Databases, Functions } from "appwrite";
import { AppProvider } from '@toolpad/core/AppProvider';
import { SignInPage } from '@toolpad/core/SignInPage';
import { useTheme, createTheme } from '@mui/material/styles';
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Link,
  IconButton,
  Button,
  Stack,
  Box,
  Typography,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';


const client = new Client()
  .setEndpoint(process.env.REACT_APP_ENDPOINT)
  .setProject(process.env.REACT_APP_PROJECT_ID);

const account = new Account(client);
const databases = new Databases(client);
const functions = new Functions(client);


const providers = [{ id: 'credentials', name: 'Email and Password' }];

function LogoButton() {
  const navigate = useNavigate();

  return (
    <Button
      variant="text"
      onClick={() => navigate('/')}
      sx={{
        position: 'relative',
        display: 'flex',
        mt: '-5%',
        mb: '5%',
        height: '2.5em',
        width: 'fit-content',
        alignItems: 'center'
      }}
    >
      <ArrowBackIosNewOutlinedIcon sx={{ mr: '0.5em', fontSize: '1em' }} />
      Home&nbsp;
    </Button>
  );
}

const BRANDING = {
  logo: (<>
    <LogoButton />
    <Stack direction="row" justifyContent="center" alignItems="center" gap={1.5}>
      <img
        src={logo}
        alt="Takeoff Monkey Widgets Logo"
        style={{ height: 48 }}
      />
    </Stack>
  </>),
  title: 'Takeoff Monkey Widgets',
};


export default function NewTeam() {
  const navigate = useNavigate();
  const [session, setSession] = useAtom(UserSession);
  const [inviteData, setInviteData] = useState(false);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("info");
  const [creating, setCreating] = useState(false);

  const name = useRef()

  const urlParams = new URLSearchParams(window.location.search);
  const inviteKey = urlParams.get('key');


  useEffect(() => {
    if (!inviteKey) {
      navigate('/');
      return;
    }

    const getSession = async () => {
      try {
        const user = await account.get()
        if (user) setSession(user);
      } catch (error) {
        setSession(null);
      }

      // Get active invites to determine if this user can create team
      try {
        await functions.createExecution(
          "db-get-document",
          JSON.stringify({
            'database_id': masterTeam,
            'collection_id': 'invites',
            'document_id': inviteKey
          }),
          false,
          '/',
          'GET'
        ).then((data) => {
          const getInviteData = JSON.parse(data.responseBody).response
          if (getInviteData) setInviteData(getInviteData);
          setLoading(false);
        })
      } catch (error) {
        setInviteData(false);
      }
    }
    getSession();
  }, []);

  const handleSignIn = async (provider, formData, callbackUrl) => {
    const teamName = name?.current?.value

    try {
      // Validate team name
      if (teamName.trim() === '') {
        setAlertType("warning");
        setOpen("Please enter a team name.");
        return;
      }

      if (creating) {
        setAlertType("warning");
        setOpen("A team is already in the process of being created...");
        return;
      }

      if (inviteData?.roles.length === 0) {
        setAlertType("info");
        setOpen("Creating team... (this make take a minute)");
        setCreating(true);

        // Create new team
        // await createTeam(teamName, session?.$id, session?.name, session?.email, null, true)
        await createTeam(teamName, session?.$id, null, true)
          .catch((error) => {
            console.error(error);
            setAlertType("error");
            setOpen("Error creating team: " + error.message);
            setCreating(false);
            return;
          })

        // Delete the invite and redirect to team homepage
        await functions.createExecution(
          "db-delete-document",
          JSON.stringify({
            'database_id': masterTeam,
            'collection_id': 'invites',
            'document_id': inviteKey
          }),
          false,
          '/',
          'GET'
        ).then(() => {
          navigate(`/?team=${sanitize(teamName)}`);
        }).catch((error) => {
          console.error(error);
          setAlertType("error");
          setOpen("Error creating team: " + error.message);
          setCreating(false);
        })
      } else {
        setAlertType("error");
        setOpen("Error creating team: Invalid invite.");
        setCreating(false);
      }
    } catch (error) {
      console.error(error);
      setAlertType("error");
      setOpen("Error joining team: " + error.message);
    }
  }


  return (<>
    <AppProvider branding={BRANDING}>
      <Stack direction="row" sx={{ maxWidth: '500px', mx: 'auto', my: '1.5em' }}>
        <SignInPage
          signIn={handleSignIn}
          providers={providers}
          slotProps={{
            emailField: {
              label: 'Username',
              name: 'username',
              type: 'text',
              autoFocus: false,
              required: true,
            },
            passwordField: {
              label: 'Password',
              name: 'password',
              type: 'password',
            },
            form: {
              noValidate: true,
              sx: { '& .MuiInputBase-root': { height: '2.5em', overflow: 'hidden' } }
            },
            submitButton: {
              sx: { mt: 2 }
            }
          }}
          slots={{
            title: () => <Typography
              variant="h4"
              sx={{ mt: 1, mb: 1, textAlign: 'center' }}
            >
              Create new team
            </Typography>,

            ...(loading ? { subtitle: () => <Stack>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  overflow: 'hidden'
                }}>
                  <CircularProgress />
              </Box>
              <Typography variant="body1" sx={{ mt: 2 }}>Checking data, just a moment...</Typography>
            </Stack>} :
            { subtitle: () => <Stack>
              {!inviteData || (inviteData && inviteData.roles?.length > 0) ?
                <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
                  This team creation key is expired or invalid.
                </Alert> :

              !session ?
                <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
                  You are not logged into an account.&nbsp;
                  <br /><Link href={`/login?redirect=${encodeURIComponent(`/new-team?key=${inviteKey}`)}`}>
                    Log in
                  </Link>
                  &nbsp;or&nbsp;
                  <Link href={`/welcome?redirect=${encodeURIComponent(`/new-team?key=${inviteKey}`)}`}>
                    create new account
                  </Link>
                </Alert> :

              inviteData && inviteData.$databaseId && inviteData.roles?.length === 0 &&
                <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                  Creating team as <b>{session?.name}</b> ({session?.email}).&nbsp;
                  <br /><Link href={`/login?redirect=${encodeURIComponent(`/new-team?key=${inviteKey}`)}&signout=true`}>
                    Switch account
                  </Link>
                </Alert>}
            </Stack>}),

            ...(inviteData && inviteData.roles?.length === 0 ?
              { emailField: () => <Stack>
                <Stack direction="row" sx={{ mt: 2.5 }}>
                  <Typography variant="h6" sx={{ mr: 0.5 }}>
                    Set new team name
                  </Typography>
                  <InfoTooltip text="This is how the team will appear around the site. This is permanent, and cannot be changed." />
                </Stack>
                <TextField
                  inputRef={name}
                  defaultValue={name.current?.value}
                  fullWidth
                  label="Team name"
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { height: '2.5em', overflow: 'hidden' } }}
                />
              </Stack>} :
            { emailField: () => null }),

            ...(inviteData && inviteData.roles?.length === 0 ?
              {passwordField: () => <Stack>
                <Stack direction="row" sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="h6" sx={{ mr: 0.5 }}>
                    Upload team logo <i>(optional)</i>
                  </Typography>
                  <InfoTooltip text="This is your team icon. Use a high-res, square image. You can change this later." />
                </Stack>
                <FileUpload type="PNG JPG" verticalMode disabled={!inviteData || !session}/>
              </Stack>} :
            { passwordField: () => null }),

            submitButton: () => <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={!inviteData || !session || (session && session.email !== inviteData.email) || creating || inviteData.roles?.length > 0}
              sx={{ mt: 2 }}
            >
              {creating ? <CircularProgress size={25} color='inherit' /> : "Create Team"}
            </Button>,

            rememberMe: () => null
          }}
          sx={{ mx: 'auto' }}
        />
      </Stack>
    </AppProvider>
    <Snackbar
      open={Boolean(open)}
      onClose={() => setOpen(false)}
      autoHideDuration={10000}
      key="snackbar"
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity={alertType}>{open}</Alert>
    </Snackbar>
  </>);
}